/**
 * Device pairing flow. Called once when a tech first opens the PWA.
 * Detects WebAuthn support and chooses the unlock path; falls through
 * to PIN if WebAuthn is unavailable or declined.
 */

import type {
  AuthHttp,
  DeviceRecord,
  DeviceStore,
} from './types.js';
import type {
  DevicePairRequest,
  DevicePairResponse,
} from '@argos/api-types';
import {
  deriveKeyFromPin,
  deriveKeyFromPrf,
  newIv,
  wrapToken,
  base64urlDecode,
  base64urlEncode,
} from './crypto.js';

export interface PairOptions {
  email: string;
  password: string;
  deviceName: string;
  platform: 'ios-pwa' | 'android-pwa' | 'web';
  /** Six-digit PIN. Required even when WebAuthn enrolls — it's the
   *  fallback for OS dialog dismissals and devices without biometrics. */
  pin: string;
  http: AuthHttp;
  store: DeviceStore;
  /** RP ID for WebAuthn (e.g. "field.argostellus.com"). */
  rpId: string;
  rpName: string;
  /** When false, skip WebAuthn entirely and use PIN-only. Useful for
   *  legacy browsers or when the user explicitly opts out. */
  enableWebAuthn?: boolean;
}

export interface PairResult {
  device: DeviceRecord;
  unlockMethod: 'webauthn' | 'pin';
}

/**
 * Probe whether the runtime can do platform-bound WebAuthn (Face ID,
 * Touch ID, Android fingerprint). Returns false on iOS <16.4, on
 * desktop browsers without a platform authenticator, etc.
 */
export async function isPlatformWebAuthnAvailable(): Promise<boolean> {
  if (typeof PublicKeyCredential === 'undefined') return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export async function pairDevice(opts: PairOptions): Promise<PairResult> {
  if (opts.pin.length < 6) {
    throw new Error('PIN must be at least 6 digits');
  }

  // 1. Authenticate + register the device server-side.
  const pairBody: DevicePairRequest = {
    username: opts.email,
    password: opts.password,
    device_name: opts.deviceName,
    platform: opts.platform,
  };
  const pair = await opts.http.postJson<DevicePairRequest, DevicePairResponse>(
    '/api/v1/auth/device/pair',
    pairBody,
  );

  const salt = base64urlDecode(pair.salt);
  const iv = newIv();

  // 2. Try WebAuthn first; fall back to PIN.
  const wantWebAuthn = opts.enableWebAuthn !== false && (await isPlatformWebAuthnAvailable());

  if (wantWebAuthn) {
    try {
      const enrolled = await enrollWebAuthn(opts, pair, iv);
      if (enrolled) {
        await opts.store.save(enrolled.record);
        return { device: enrolled.record, unlockMethod: 'webauthn' };
      }
    } catch (err) {
      // Surface to telemetry but don't block — fall through to PIN.
      console.warn('WebAuthn enrollment failed; falling back to PIN', err);
    }
  }

  // 3. PIN path.
  const pinKey = await deriveKeyFromPin(opts.pin, salt);
  const wrapped = await wrapToken(pinKey, iv, pair.device_token);

  const record: DeviceRecord = {
    device_id: pair.device_id,
    user: pair.user,
    paired_at: new Date().toISOString(),
    expires_at: pair.expires_at,
    unlock_method: 'pin',
    salt,
    iv,
    wrapped_token: wrapped,
    pin_attempts: 0,
  };
  await opts.store.save(record);
  return { device: record, unlockMethod: 'pin' };
}

interface WebAuthnEnrollment {
  record: DeviceRecord;
}

async function enrollWebAuthn(
  opts: PairOptions,
  pair: DevicePairResponse,
  iv: Uint8Array,
): Promise<WebAuthnEnrollment | null> {
  // Server-issued challenge for credential creation.
  const optionsResp = await opts.http.postJson<
    { device_id: string },
    { challenge: string }
  >('/api/v1/auth/device/webauthn/register/options', { device_id: pair.device_id });

  const challenge = base64urlDecode(optionsResp.challenge);
  const userIdBytes = base64urlDecode(pair.device_id);

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: challenge as unknown as BufferSource,
      rp: { id: opts.rpId, name: opts.rpName },
      user: {
        id: userIdBytes as unknown as BufferSource,
        name: pair.user.email,
        displayName: pair.user.full_name ?? pair.user.email,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'required',
      },
      timeout: 60_000,
      // Request the PRF extension so we can derive a wrapping key from
      // the authenticator. Browsers without PRF return undefined here
      // and we'll fall back to PIN-only.
      extensions: { prf: { eval: { first: new Uint8Array([1]) } } } as Record<string, unknown>,
    },
  })) as PublicKeyCredential | null;

  if (!credential) return null;

  const credentialId = new Uint8Array(credential.rawId);
  const attestation = credential.response as AuthenticatorAttestationResponse;
  const extensionResults = (credential as unknown as {
    getClientExtensionResults?: () => Record<string, unknown>;
  }).getClientExtensionResults?.() as
    | { prf?: { results?: { first?: ArrayBuffer } } }
    | undefined;

  // Verify attestation server-side.
  await opts.http.postJson<unknown, { ok: true }>(
    '/api/v1/auth/device/webauthn/register/verify',
    {
      device_id: pair.device_id,
      credential_id: base64urlEncode(credentialId),
      attestation_object: base64urlEncode(new Uint8Array(attestation.attestationObject)),
      client_data_json: base64urlEncode(new Uint8Array(attestation.clientDataJSON)),
    },
  );

  const prfFirst = extensionResults?.prf?.results?.first;
  if (!prfFirst) {
    // Authenticator registered but doesn't support PRF — we still keep
    // the credential ID for proof of possession during unlock, but the
    // actual wrapping key comes from the PIN. Hybrid: PIN unlocks, but
    // user-verification on the authenticator is required first.
    return null;
  }

  const key = await deriveKeyFromPrf(prfFirst);
  const wrapped = await wrapToken(key, iv, pair.device_token);

  const salt = base64urlDecode(pair.salt);
  const record: DeviceRecord = {
    device_id: pair.device_id,
    user: pair.user,
    paired_at: new Date().toISOString(),
    expires_at: pair.expires_at,
    unlock_method: 'webauthn',
    salt,
    iv,
    wrapped_token: wrapped,
    pin_attempts: 0,
    credential_id: credentialId,
    prf_supported: true,
  };
  return { record };
}
